import React, { useMemo, useRef, useState } from 'react';
import { CheckCircle2, Loader, ShieldCheck, AlertTriangle } from 'lucide-react';
import Sheet from '../common/Sheet';
import PinInput from './PinInput';
import BiometricSetup from './BiometricSetup';
import InstallAppButton from '../auth/InstallAppButton';
import { useAuth } from '../../context/authcontext';
import { useEntitlement } from '../../hooks/useEntitlement';
import { useDeviceClass, useOnlineStatus } from '../../hooks/useOfflineDevice';
import { useCanPerform } from '../../hooks/useCanPerform';
import { pinProblem } from '../../services/offline/crypto';
import { enrollThisDevice, EnrollmentError } from '../../services/offline/enrollment';
import { detectPlatform, isStandalone } from '../../services/offline/platform/deviceClass';
import { enrollmentProblems } from '../../services/offline/platform/storageReadiness';

export interface OfflineEnrollmentSheetProps {
  open: boolean;
  onClose: () => void;
  onEnrolled?: () => void;
}

type Step = 'pin' | 'busy' | 'done';

/**
 * Set up offline mode on this phone or tablet (Phase 16A, design §6.3). Checks the device first
 * (installed app, connection, Core plan or above), then asks for a 6-digit PIN twice.
 */
const OfflineEnrollmentSheet: React.FC<OfflineEnrollmentSheetProps> = ({ open, onClose, onEnrolled }) => {
  const { user } = useAuth();
  const planIncludesOffline = useEntitlement('offlineMode');
  const deviceClass = useDeviceClass();
  const online = useOnlineStatus();
  const canStartVisits = useCanPerform('create:visit');
  const [step, setStep] = useState<Step>('pin');
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ packets: number | null; packetError?: string } | null>(null);
  // 16D D2: the chosen PIN is kept in memory only until the sheet closes, for the optional biometric step
  const pinForBiometric = useRef('');

  const problems = useMemo(() => enrollmentProblems({
    deviceClass,
    platform: detectPlatform(),
    standalone: isStandalone(),
    online,
    planIncludesOffline,
    canStartVisits,
    cryptoAvailable: Boolean(globalThis.crypto?.subtle),
    indexedDbAvailable: typeof indexedDB !== 'undefined',
  }), [deviceClass, online, planIncludesOffline, canStartVisits]);

  const close = () => {
    if (step === 'busy') return;
    setPin(''); setConfirm(''); setError(null); setStep('pin'); setResult(null);
    pinForBiometric.current = '';
    onClose();
  };

  const submit = async () => {
    if (!user) return;
    const problem = pinProblem(pin);
    if (problem) { setError(problem); return; }
    if (pin !== confirm) { setError('The two PINs don\'t match'); return; }
    setError(null);
    setStep('busy');
    try {
      setResult(await enrollThisDevice(user, pin));
      pinForBiometric.current = pin;
      setStep('done');
      onEnrolled?.();
    } catch (e) {
      setStep('pin');
      setError(e instanceof EnrollmentError || e instanceof Error ? e.message : 'Offline mode could not be set up');
    } finally {
      setPin(''); setConfirm('');
    }
  };

  const footer = step === 'done'
    ? <button type="button" onClick={close} className="min-h-[48px] w-full rounded-lg bg-slate-800 text-base font-semibold text-white">Done</button>
    : problems.length === 0 && (
      <button type="button" onClick={submit} disabled={step === 'busy' || pin.length !== 6 || confirm.length !== 6} className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 text-base font-semibold text-white disabled:opacity-50">
        {step === 'busy' ? <><Loader size={18} className="animate-spin" />Setting up…</> : 'Turn on offline mode'}
      </button>
    );

  return (
    <Sheet open={open} onClose={close} title="Offline mode" subtitle="Keep working on this device without signal" footer={footer || undefined} dismissible={step !== 'busy'}>
      {step === 'done' ? (
        <div className="space-y-3 text-sm text-gray-800">
          <p className="flex items-center gap-2 text-base font-semibold text-emerald-800"><CheckCircle2 size={20} />Offline mode is on</p>
          {result?.packets !== null && result?.packets !== undefined
            ? <p>{result.packets} {result.packets === 1 ? 'patient is' : 'patients are'} ready for offline use.</p>
            : <p className="text-amber-800">Your patients will download the next time you're online on My Day.</p>}
          <p>When the app locks or you reopen it, unlock it with your PIN.</p>
          {pinForBiometric.current && <BiometricSetup pin={pinForBiometric.current} />}
        </div>
      ) : problems.length > 0 ? (
        <div className="space-y-3">
          <ul className="space-y-2">
            {problems.map((p) => (
              <li key={p.id} className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />{p.message}
              </li>
            ))}
          </ul>
          {problems.some((p) => p.id === 'install') && <InstallAppButton variant="inline" />}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3 text-sm text-gray-700">
            <ShieldCheck size={20} className="mt-0.5 shrink-0 text-emerald-700" aria-hidden="true" />
            <span>Today's and tomorrow's patients are stored encrypted on this device. Use this only on a device with a screen lock that you don't share outside the agency.</span>
          </div>
          <PinInput id="enroll-pin" label="Choose a 6-digit PIN" value={pin} onChange={setPin} disabled={step === 'busy'} autoFocus />
          <PinInput id="enroll-pin-confirm" label="Enter the PIN again" value={confirm} onChange={setConfirm} disabled={step === 'busy'} />
          <p className="text-xs text-gray-500">Five wrong PINs erase offline data from this device.</p>
          {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        </div>
      )}
    </Sheet>
  );
};

export default OfflineEnrollmentSheet;
